import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useCallback, useEffect, useRef, useState } from "react"
import { DistanceUnit, SidePanelLayerType, zIndex } from "@nova/constants"
import { config } from "@nova/configuration/browser"
import classNames from "classnames"
import {
    selectLiveVideoFlightById,
    selectVideoFlightById,
} from "features/entity-caching/flights/selectors"
import * as Icons from "@nova/component-icons"
import { NovaSpinner } from "@nova/component-nova-spinner"
import { ButtonBorderless } from "@nova/component-btn"
import { Controls } from "plugins/Panorama/Controls"
import { InspectorPanelAccordion } from "features/map/inspector-panel/accordion/InspectorPanelAccordion"
import { InspectorPanelKeyValueToggle } from "features/map/inspector-panel/InspectorPanelKeyValueToggle"
import { LiveVideoQRCode } from "../LiveVideo/LiveVideoQRCode"
import { AutoHeightRnD } from "@nova/component-auto-height-rnd"
import { zoomToShowPoint } from "features/map/control/slice"
import { selectEntityById } from "panels/content/side-panel/slice"
import {
    selectIsCrosshairEnabledForFlightId,
    selectIsDroneIconEnabledForFlightId,
    selectIsFlightPathEnabledForFlightId,
    selectIsFrameEnabledForFlightId,
    selectOpenVideoFlights,
} from "../state/selectors"
import { toggleCrosshair, toggleDroneIcon, toggleFlightPath, toggleFrame } from "../state/slice"
import { closeVideoFlight, openVideoFlight } from "../state/thunks"
import {
    selectFollowingEntityId,
    selectPictureInPictureId,
} from "plugins/shared/playback/state/selectors"
import { setFollowingEntity, setPictureInPictureId } from "plugins/shared/playback/state/slice"
import { SegmentedControl } from "panels/content/top-bar/TODO-put-in-package/SegmentedControl"
import { VideoFlightController } from "../controllers/VideoFlightController"
import {
    useGuardedVideoControllerForFlight,
    useVideoControllerForFlight,
} from "../controllers/hooks/useController"
import {
    useCanProjectVideo,
    useHasPlayedVideo,
    useIsAnonymousFlightFinished,
    useIsCameraOff,
    useIsStandby,
    useIsStreamDisconnected,
    useIsWaitingForRecording,
    useIsWaitingForStreamToStart,
    useVideoCurrentFrameData,
    useVideoData,
    useVideoDroneColor,
    useVideoResolutions,
} from "../controllers/hooks/useControllerEvents"
import { get } from "nodash"
import { selectUserEmail } from "user/profile/selectors"
import { formatTime, TimeFormat } from "@nova/format"
import { useConfirmationModalContext } from "features/modals/WorkspaceConfirmationModalV2"
import {
    ShareActiveLiveStreamArgs,
    ShareActiveLiveStreamModal,
    useShareLiveStream,
} from "features/multi-stream/ShareLiveStreamModal"
import { Tooltip } from "panels/content/top-bar/TODO-put-in-package/Tooltip"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { VideoAudioControls, VOLUME_CONTROL_SLIDER_CLASS } from "./VideoAudioControls"

const LANDSCAPE_MIN_WIDTH = 490
const PORTRAIT_MIN_WIDTH = 220
// Between 0 and 1, target percentage that the height of the Rnd container should be relative to the window.
const DEFAULT_HEIGHT_PERCENTAGE_OF_WINDOW = 0.55
const MAX_STARTING_OFFSET = 32 * 5

export function VideoContainer({ flightId }: { flightId: string }) {
    const shutdown = useRef(false)
    const dispatch = useAppDispatch()
    const flight = useAppSelector(selectEntityById(flightId))
    const includePiPControls = flight?.type === SidePanelLayerType.LiveVideoFlight
    const { isEditor } = useWorkspacePermissions()

    const pictureInPictureId = useAppSelector(selectPictureInPictureId)
    const isPictureInPicture = pictureInPictureId === flightId
    const openVideoFlights = useAppSelector(selectOpenVideoFlights)
    const controller = useVideoControllerForFlight({ flightId })

    const confirmationModal = useConfirmationModalContext()

    useEffect(() => {
        controller?.fetchAllData(true)
    }, [controller])

    const contentRef = useRef<HTMLDivElement>(null)
    const [width, setWidth] = useState(LANDSCAPE_MIN_WIDTH)

    // Handle video rotation mid-stream to adjust the minWidth value accordingly.
    const [aspectRatio, setAspectRatio] = useState(16 / 9)
    const minWidth = aspectRatio >= 1 ? LANDSCAPE_MIN_WIDTH : PORTRAIT_MIN_WIDTH
    useEffect(() => {
        const maxWidthToFit = Math.round(
            window.innerHeight * DEFAULT_HEIGHT_PERCENTAGE_OF_WINDOW * aspectRatio
        )
        setWidth((prev) => Math.max(minWidth, Math.min(prev, maxWidthToFit)))
    }, [aspectRatio, minWidth])

    const [position, setPosition] = useState({
        x: 8 + Math.min((openVideoFlights.length - 1) * 32, MAX_STARTING_OFFSET),
        y: 8 + Math.min((openVideoFlights.length - 1) * 32, MAX_STARTING_OFFSET),
    })

    const userDomain = useAppSelector(selectUserEmail)?.split("@").at(-1)
    const isMapNovaOrHummingbird =
        userDomain === "mapnova.com" || userDomain === "hummingbirddrones.ca"

    const { shareLiveStream } = useShareLiveStream()

    if (!flight) {
        if (!shutdown.current) {
            dispatch(closeVideoFlight({ flightId }))
            shutdown.current = true
        }
        return null
    }

    const allowShare = get(flight, "live") && isEditor

    if (isPictureInPicture) {
        return (
            <div
                className="absolute bg-gray-800 rounded-lg w-full h-full flex flex-col !pointer-events-auto border border-gray-700 p-2"
                style={{
                    zIndex: zIndex.MapOverlayContainers,
                }}
            >
                <VideoFlightInfoBar flightId={flightId} />
                <div className="rounded-lg overflow-hidden relative flex flex-col h-full w-full">
                    <VideoFlightPlayer
                        flightId={flightId}
                        includePiPControls={includePiPControls}
                        includeAudioControls
                    />
                </div>
                <div className="w-full flex mt-2 mb-2">
                    <div className="mt-2 flex gap-2 w-[40%] min-w-[600px] items-center pr-2">
                        <LiveVideoStats flightId={flightId} />
                        {(allowShare || isMapNovaOrHummingbird) && (
                            <ButtonBorderless
                                title="Share"
                                RightIcon={Icons.Share}
                                height="1.5rem"
                                className="-mr-2"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    confirmationModal.confirm<ShareActiveLiveStreamArgs>({
                                        RenderComponent: ({
                                            confirm,
                                            cancel,
                                            isPerformingAsyncConfirmCallback,
                                        }) => (
                                            <ShareActiveLiveStreamModal
                                                confirm={confirm}
                                                cancel={cancel}
                                                isPerformingAsyncConfirmCallback={
                                                    isPerformingAsyncConfirmCallback
                                                }
                                                flightId={flightId}
                                            />
                                        ),
                                        asyncConfirmCallback: async ({
                                            type,
                                            selectedEmails,
                                            selectedPhoneNumbers,
                                            selectedMissionGroupIds,
                                        }) => {
                                            if (type === "all-stream") {
                                                await shareLiveStream({
                                                    type: "all-streams",
                                                    selectedEmails,
                                                    selectedPhoneNumbers,
                                                    selectedMissionGroupIds: [],
                                                })
                                            } else if (type === "single-stream") {
                                                await shareLiveStream({
                                                    type: "single-stream",
                                                    flightId,
                                                    selectedEmails,
                                                    selectedPhoneNumbers,
                                                })
                                            } else {
                                                await shareLiveStream({
                                                    type: "mission-group",
                                                    flightId,
                                                    selectedMissionGroupIds:
                                                        selectedMissionGroupIds ?? [],
                                                })
                                            }
                                        },
                                    })
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>
        )
    }

    if (!!pictureInPictureId) return null

    return (
        <div
            className="absolute pointer-events-none inset-0"
            style={{
                zIndex: zIndex.LiveVideo,
            }}
            onMouseDown={() => dispatch(openVideoFlight({ flightId }))} // brings it to the front
        >
            <AutoHeightRnD
                position={position}
                setPosition={setPosition}
                width={width}
                minWidth={minWidth}
                setWidth={setWidth}
                contentRef={contentRef}
                className="border border-white-200"
                cancel={`.${VOLUME_CONTROL_SLIDER_CLASS}`}
            >
                <VideoFlightInfoBar flightId={flightId} useGrabCursor />
                <div className="rounded-lg overflow-hidden border border-gray-700 relative">
                    <VideoFlightPlayer
                        flightId={flightId}
                        includePiPControls={includePiPControls}
                        includeAudioControls
                        onAspectRatioChange={setAspectRatio}
                    />
                    <LiveVideoStats flightId={flightId} />
                </div>
                <div className="mt-2">
                    <VideoFlightMoreOptionsAccordion flightId={flightId} />
                    <LiveVideoFlightMoreOptionsAccordion flightId={flightId} />
                </div>
            </AutoHeightRnD>
        </div>
    )
}

const VideoFlightInfoBar = ({
    flightId,
    useGrabCursor,
}: {
    flightId: string
    useGrabCursor?: boolean
}) => {
    const dispatch = useAppDispatch()
    const controller = useVideoControllerForFlight({ flightId })
    const droneColor = useVideoDroneColor({ flightId })
    const flight = useAppSelector(selectEntityById(flightId))
    const liveVideoFlight = useAppSelector(selectLiveVideoFlightById(flightId))
    const isFollowing = useAppSelector(selectFollowingEntityId) === flightId
    useEffect(() => {
        if (isFollowing) {
            return () => {
                dispatch(setFollowingEntity(undefined))
            }
        }
    }, [dispatch, isFollowing])
    return (
        <div
            className={classNames("flex gap-2 items-center w-full mb-2", {
                "cursor-grab": useGrabCursor,
            })}
        >
            <Icons.SmallIcon
                Icon={Icons.DroneVideoIcon}
                style={{
                    color: droneColor,
                }}
            />
            <h4 className="my-0 text-white">{flight?.name || "Video"}</h4>
            {!liveVideoFlight?.isMobile && (
                <div className="relative group/tooltip">
                    <Icons.SmallIcon
                        Icon={
                            isFollowing
                                ? Icons.AssistantNavigationFilled
                                : Icons.AssistantNavigation
                        }
                        className={classNames("cursor-pointer", {
                            "text-gray-100 hover:text-white": !isFollowing,
                            "text-blue-500": isFollowing,
                        })}
                        onClick={() => {
                            dispatch(
                                setFollowingEntity(
                                    isFollowing ? undefined : { type: "video", id: flightId }
                                )
                            )
                        }}
                    />
                    <Tooltip title={isFollowing ? "Stop following" : "Follow"} />
                </div>
            )}
            <div className="relative group/tooltip">
                <Icons.ExtraSmallIcon
                    Icon={Icons.ZoomTo}
                    className="text-gray-100 hover:text-gray-0 cursor-pointer"
                    onClick={() => {
                        const currentFrameData = controller?.getCurrentFrameData()
                        if (currentFrameData) {
                            dispatch(
                                zoomToShowPoint({
                                    lng: currentFrameData.longitude,
                                    lat: currentFrameData.latitude,
                                })
                            )
                        }
                    }}
                />
                <Tooltip title="Zoom to location" />
            </div>
            <div className="flex-grow" />
            <Icons.SmallIcon
                Icon={Icons.XClose}
                className="cursor-pointer text-gray-100 hover:text-gray-0"
                onClick={() => dispatch(closeVideoFlight({ flightId }))}
            />
        </div>
    )
}

const VideoPlayerControls = ({ flightId }: { flightId: string }) => {
    const dispatch = useAppDispatch()
    const isPictureInPicture = useAppSelector(selectPictureInPictureId) === flightId
    return (
        <Controls
            className="absolute z-[3] top-2 left-2"
            items={[
                {
                    Icon: Icons.PanoramaTopLeft,
                    isLargeIcon: true,
                    isSelected: !isPictureInPicture,
                    onClick: () => {
                        dispatch(setPictureInPictureId({ id: undefined }))
                    },
                },
                {
                    Icon: Icons.PictureInPicture,
                    isLargeIcon: true,
                    isSelected: isPictureInPicture,
                    onClick: () => {
                        dispatch(setPictureInPictureId({ id: flightId }))
                    },
                },
            ]}
        />
    )
}

export const VideoFlightPlayer = ({
    flightId,
    includePiPControls,
    includeAudioControls = false,
    anonymous,
    controls = false,
    onAspectRatioChange,
}: {
    flightId: string
    includePiPControls: boolean
    includeAudioControls?: boolean
    anonymous?: true
    controls?: boolean
    onAspectRatioChange?: (ratio: number) => void
}) => {
    const controller = useVideoControllerForFlight({ flightId, options: { anonymous } })
    const hasPlayed = useHasPlayedVideo({ flightId, options: { anonymous } })
    const isAnonStreamFinished = useIsAnonymousFlightFinished({ flightId, options: { anonymous } })
    const isWaitingForRecording = useIsWaitingForRecording({ flightId, options: { anonymous } })
    const isWaitingForStreamToStart = useIsWaitingForStreamToStart({
        flightId,
        options: { anonymous },
    })
    const isStandby = useIsStandby({ flightId })
    const isStreamDisconnected = useIsStreamDisconnected({ flightId, options: { anonymous } })
    const isCameraOff = useIsCameraOff({ flightId, options: { anonymous } })
    const liveVideoFlight = useAppSelector(selectLiveVideoFlightById(flightId))
    const expiresAt = liveVideoFlight ? new Date((liveVideoFlight.createdAt + 3600) * 1000) : null
    const videoRef = useRef<HTMLVideoElement>(null)
    const [isHovering, setIsHovering] = useState(false)

    // Forwards live video dimension changes as aspect ratio to parent for resizing math.
    useEffect(() => {
        const video = videoRef.current
        if (!video || !onAspectRatioChange) return
        const handleDimensionChange = () => {
            if (video.videoWidth && video.videoHeight) {
                onAspectRatioChange(video.videoWidth / video.videoHeight)
            }
        }
        if (video.readyState >= 1) handleDimensionChange()
        video.addEventListener("loadedmetadata", handleDimensionChange)
        video.addEventListener("resize", handleDimensionChange)
        return () => {
            video.removeEventListener("loadedmetadata", handleDimensionChange)
            video.removeEventListener("resize", handleDimensionChange)
        }
    }, [onAspectRatioChange])

    useEffect(() => {
        const curVideoRef = videoRef.current
        if (
            !curVideoRef ||
            !controller ||
            isStandby ||
            isWaitingForRecording ||
            isWaitingForStreamToStart ||
            isAnonStreamFinished ||
            !videoRef.current
        )
            return
        const detach = controller.attachToElement(videoRef.current)
        return () => {
            detach()
        }
    }, [
        controller,
        isAnonStreamFinished,
        isStandby,
        isWaitingForRecording,
        isWaitingForStreamToStart,
    ])

    return (
        <div
            className="relative w-full h-full flex items-center justify-center text-center"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
        >
            {isCameraOff && hasPlayed && <div className="absolute inset-0 z-[2] bg-black" />}
            {isStreamDisconnected && hasPlayed && (
                <div className="absolute inset-0 z-[3] flex flex-col items-center justify-center bg-gray-900 animate-fade-in">
                    <Icons.VideoOff className="w-6 h-6 text-gray-400 mb-4" />
                    <div className="text-white text-base font-normal leading-[1.2]">
                        Connection Lost
                    </div>
                    <div className="text-gray-300 text-sm font-normal leading-[1.4] text-center">
                        No signal received
                    </div>
                </div>
            )}
            {isStandby ? (
                <div className="text-white p-10">
                    <div>Ready to Live Stream</div>
                    <div className="text-gray-300 text-center text-sm font-normal leading-[140%]">
                        {expiresAt
                            ? `Layer will expire at ${formatTime({ value: expiresAt, format: TimeFormat.TwelveHour })}`
                            : "Layer will expire in 1 hour"}
                    </div>
                </div>
            ) : isWaitingForRecording ? (
                <div className="text-white p-10 animate-fade-in">
                    <div>Stream has ended.</div>
                    <div>Waiting for recording to process.</div>
                </div>
            ) : isAnonStreamFinished ? (
                <div className="text-white p-10 flex gap-2 items-center">
                    <Icons.Icon Icon={Icons.Information} />
                    <div>Stream is no longer available</div>
                </div>
            ) : !hasPlayed ? (
                <div className="p-8">
                    <NovaSpinner width={32} height={32} />
                </div>
            ) : (
                <></>
            )}
            <video
                ref={videoRef}
                autoPlay
                playsInline
                controls={controls}
                muted
                className={classNames("object-contain", {
                    "w-0 h-0 opacity-0 absolute pointer-events-none":
                        isStandby || !hasPlayed || isWaitingForRecording,
                    "w-full h-full opacity-1 static":
                        !isStandby && !!hasPlayed && !isWaitingForRecording,
                })}
            />
            {includePiPControls && isHovering && <VideoPlayerControls flightId={flightId} />}
            {includeAudioControls && (
                <VideoAudioControls
                    flightId={flightId}
                    options={{ anonymous }}
                    className="absolute z-[3] bottom-2 left-2"
                />
            )}
        </div>
    )
}

const LiveVideoStats = ({ flightId }: { flightId: string }) => {
    const { organizationSettingDistance } = config.use("organizationSettingDistance") as {
        organizationSettingDistance: DistanceUnit
    }
    const convertMetersToWorkspaceUnit = useCallback(
        (d: number | undefined, fixed = 2) => {
            if (typeof d !== "number") return ["", "-"]
            if (organizationSettingDistance === DistanceUnit.Meters) return [d.toFixed(fixed), "m"]
            else return [(d * 3.28084).toFixed(fixed), "ft"]
        },
        [organizationSettingDistance]
    )
    const currentFrame = useVideoCurrentFrameData({ flightId })
    const liveVideoFlight = useAppSelector(selectLiveVideoFlightById(flightId))
    const isPictureInPicture = useAppSelector(selectPictureInPictureId) === flightId
    if (!liveVideoFlight || liveVideoFlight.isMobile) return null

    const convertedHeight = convertMetersToWorkspaceUnit(
        currentFrame?.height ?? currentFrame?.elevation,
        1
    )
    const convertedHorizontalSpeed = convertMetersToWorkspaceUnit(currentFrame?.horizontalSpeed)
    const convertedVerticalSpeed = convertMetersToWorkspaceUnit(currentFrame?.verticalSpeed)

    return (
        <div
            className={classNames("flex divide-x divide-gray-700 border-gray-700", {
                "border-t": !isPictureInPicture,
                "flex-grow border rounded-lg": isPictureInPicture,
            })}
        >
            <LiveVideoStat
                title={`Altitude ${currentFrame?.height === undefined ? "ATL" : "ASL"}`}
                value={`${convertedHeight[0]} ${convertedHeight[1]}`}
            />
            <LiveVideoStat
                title="Vertical Speed"
                value={`${convertedVerticalSpeed[0]} ${convertedVerticalSpeed[1]}/s`}
            />
            <LiveVideoStat
                title="Horizontal Speed"
                value={`${convertedHorizontalSpeed[0]} ${convertedHorizontalSpeed[1]}/s`}
            />
        </div>
    )
}

const LiveVideoStat = ({ title, value }: { title: string; value: string }) => {
    return (
        <div className="flex-1 px-3 py-1.5">
            <small className="text-gray-300">{title}</small>
            <h4 className="text-gray-0">{value}</h4>
        </div>
    )
}

const LiveVideoFlightMoreOptionsAccordion = ({ flightId }: { flightId: string }) => {
    const dispatch = useAppDispatch()
    const [isOpen, setIsOpen] = useState(false)
    const userDomain = useAppSelector(selectUserEmail)?.split("@").at(-1)
    const isMapNovaOrHummingbird =
        userDomain === "mapnova.com" || userDomain === "hummingbirddrones.ca"
    const flightPathEnabled = useAppSelector(selectIsFlightPathEnabledForFlightId(flightId))
    const droneIconEnabled = useAppSelector(selectIsDroneIconEnabledForFlightId(flightId))
    const liveVideoFlight = useAppSelector(selectLiveVideoFlightById(flightId))
    const { isEditor } = useWorkspacePermissions()
    const allowShare = liveVideoFlight?.live && isEditor
    const confirmationModal = useConfirmationModalContext()
    const { shareLiveStream } = useShareLiveStream()
    if (!liveVideoFlight || (liveVideoFlight.isMobile && !!liveVideoFlight.endedAt)) return null
    return (
        <InspectorPanelAccordion
            title="More Options"
            isOpen={isOpen}
            setIsOpen={(isOpen) => {
                setIsOpen(isOpen)
            }}
            RightItem={
                allowShare || isMapNovaOrHummingbird ? (
                    <ButtonBorderless
                        title="Share"
                        RightIcon={Icons.Share}
                        height="1.5rem"
                        className="-mr-2"
                        onClick={(e) => {
                            e.stopPropagation()
                            confirmationModal.confirm<ShareActiveLiveStreamArgs>({
                                RenderComponent: ({
                                    confirm,
                                    cancel,
                                    isPerformingAsyncConfirmCallback,
                                }) => (
                                    <ShareActiveLiveStreamModal
                                        confirm={confirm}
                                        cancel={cancel}
                                        isPerformingAsyncConfirmCallback={
                                            isPerformingAsyncConfirmCallback
                                        }
                                        flightId={flightId}
                                    />
                                ),
                                asyncConfirmCallback: async ({
                                    type,
                                    selectedEmails,
                                    selectedPhoneNumbers,
                                    selectedMissionGroupIds,
                                }) => {
                                    if (type === "all-stream") {
                                        await shareLiveStream({
                                            type: "all-streams",
                                            selectedEmails,
                                            selectedPhoneNumbers,
                                            selectedMissionGroupIds: [],
                                        })
                                    } else if (type === "single-stream") {
                                        await shareLiveStream({
                                            type: "single-stream",
                                            flightId,
                                            selectedEmails,
                                            selectedPhoneNumbers,
                                        })
                                    } else {
                                        await shareLiveStream({
                                            type: "mission-group",
                                            flightId,
                                            selectedMissionGroupIds: selectedMissionGroupIds ?? [],
                                        })
                                    }
                                },
                            })
                        }}
                    />
                ) : (
                    <></>
                )
            }
            shouldPadChildrenVertically={false}
            extraVerticalPaddingInRem={0.5}
            className="!mx-0"
        >
            <div className="flex gap-6 my-4 w-full">
                {!liveVideoFlight.isMobile && (
                    <div className={classNames("w-full", { "basis-1/3": allowShare })}>
                        <div className="h-8 flex items-center">
                            <small className="xs text-gray-0">Visibility</small>
                        </div>
                        <div
                            className={classNames("flex w-full gap-3", {
                                "flex-col": allowShare,
                                "justify-between": !allowShare,
                            })}
                        >
                            <InspectorPanelKeyValueToggle
                                keyTitle="Flight Path"
                                isToggled={flightPathEnabled}
                                setIsToggled={() => dispatch(toggleFlightPath({ flightId }))}
                                toggleClassName="justify-between"
                                className="grow"
                            />
                            <InspectorPanelKeyValueToggle
                                keyTitle="Drone Icon"
                                isToggled={droneIconEnabled}
                                setIsToggled={() => dispatch(toggleDroneIcon({ flightId }))}
                                toggleClassName="justify-between"
                                className="grow"
                            />
                        </div>
                    </div>
                )}
                {allowShare && (
                    <div
                        className={classNames({
                            "basis-2/3": !liveVideoFlight.isMobile,
                            "ml-3": liveVideoFlight.isMobile,
                        })}
                    >
                        <div className="h-8 flex items-center">
                            <small className="xs text-gray-0">View on Mobile</small>
                        </div>
                        <div className="mt-2">
                            <LiveVideoQRCode flightId={flightId} />
                        </div>
                    </div>
                )}
            </div>
        </InspectorPanelAccordion>
    )
}

const VideoFlightMoreOptionsAccordion = ({ flightId }: { flightId: string }) => {
    const dispatch = useAppDispatch()
    const [isOpen, setIsOpen] = useState(false)
    const canProject = useCanProjectVideo({ flightId })
    const resolutions = useVideoResolutions({ flightId })
    const data = useVideoData({ flightId })
    const controller = useGuardedVideoControllerForFlight({
        flightId,
        guardClass: VideoFlightController,
    })
    const flightPathEnabled = useAppSelector(selectIsFlightPathEnabledForFlightId(flightId))
    const droneIconEnabled = useAppSelector(selectIsDroneIconEnabledForFlightId(flightId))
    const crosshairEnabled = useAppSelector(selectIsCrosshairEnabledForFlightId(flightId))
    const frameEnabled = useAppSelector(selectIsFrameEnabledForFlightId(flightId))
    const videoFlight = useAppSelector(selectVideoFlightById(flightId))
    if (!videoFlight) return null
    return (
        <InspectorPanelAccordion
            isOpen={isOpen}
            setIsOpen={(isOpen) => {
                setIsOpen(isOpen)
            }}
            title="More Options"
            shouldPadChildrenVertically={false}
            extraVerticalPaddingInRem={0.5}
            className="!mx-0"
        >
            <div className="my-4 mx-4">
                {(canProject || data.length > 0) && (
                    <>
                        <div className="h-8 flex items-center">
                            <small className="xs text-gray-0">Visibility</small>
                        </div>
                        {canProject && (
                            <div className="flex gap-5">
                                <div className="flex-1">
                                    <InspectorPanelKeyValueToggle
                                        keyTitle="Crosshair"
                                        isToggled={crosshairEnabled}
                                        setIsToggled={() => dispatch(toggleCrosshair({ flightId }))}
                                        toggleClassName="justify-between"
                                    />
                                </div>

                                <div className="flex-1">
                                    <InspectorPanelKeyValueToggle
                                        keyTitle="Field of View"
                                        isToggled={frameEnabled}
                                        setIsToggled={() => dispatch(toggleFrame({ flightId }))}
                                        toggleClassName="justify-between"
                                    />
                                </div>
                            </div>
                        )}
                        {data.length > 0 && (
                            <div className="flex gap-5">
                                <div className="flex-1">
                                    <InspectorPanelKeyValueToggle
                                        keyTitle="Flight Path"
                                        isToggled={flightPathEnabled}
                                        setIsToggled={() =>
                                            dispatch(toggleFlightPath({ flightId }))
                                        }
                                        toggleClassName="justify-between"
                                    />
                                </div>

                                <div className="flex-1">
                                    <InspectorPanelKeyValueToggle
                                        keyTitle="Drone Icon"
                                        isToggled={droneIconEnabled}
                                        setIsToggled={() => dispatch(toggleDroneIcon({ flightId }))}
                                        toggleClassName="justify-between"
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )}
                {resolutions.length > 1 && (
                    <>
                        {(canProject || data.length > 0) && (
                            <hr className="mt-2 mb-4 border-t-gray-700" />
                        )}
                        <div className="flex gap-2">
                            <h5 className="self-center min-w-fit">Video Quality</h5>
                            <SegmentedControl
                                segments={["Low", "Medium", "High"]}
                                defaultSelectedIndex={1}
                                onChange={(idx) => {
                                    controller?.setCurrentResolutionIndex(idx)
                                }}
                            />
                        </div>
                    </>
                )}
            </div>
        </InspectorPanelAccordion>
    )
}
